// CHA-619 — Run detail (platform component): one run's event trail, read-only.
//
// The acceptance gap left by CHA-585 (W4): "body visible on the run". A vendor reply arrives as an
// `inbound:reply` event stored `trust: 'untrusted'`. This view shows it — and shows it AS a claim:
//   - an explicit "Unverified reply" treatment, never styled or worded like a system fact;
//   - every reply field rendered as a React text node (escaped), so an HTML or script body is inert
//     text. There is deliberately no HTML rendering path in this file.
// Whether the thing the vendor says is true is W5's job, checked against the data.
//
// Tenant-agnostic like WorkList: the tenant shell passes `tenantId`. No actions, no gateway calls,
// no state changes — the only control is Close.
import React from 'react';
import useRunEvents from '../workLayer/useRunEvents';

const fmtTime = (v) => {
  const d = v && typeof v.toDate === 'function' ? v.toDate() : (v ? new Date(v) : null);
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleString() : '—';
};

// A short, plain description of a system event's state move, from what the writer recorded.
const transitionOf = (e) => {
  const d = e.data || {};
  if (d.from && d.to) return `${d.from} → ${d.to}`;
  if (d.state) return `→ ${d.state}`;
  return null;
};

// An event is shown as a CLAIM when the writer marked it untrusted, or when it is an inbound reply
// (always untrusted by construction). Routing on `trust` too means a future untrusted event type
// (a bounce, an auto-reply) can never fall through to the system-fact rendering below.
export const isUntrusted = (e) => e?.trust === 'untrusted' || e?.type === 'inbound:reply';

function UnverifiedReply({ event }) {
  const d = event.data || {};
  const label = event.type === 'inbound:reply' ? 'Unverified reply' : `Unverified · ${String(event.type ?? 'event')}`;
  return (
    <div className="border-2 border-dashed border-carbon-gray-50 bg-carbon-gray-10 px-4 py-3" data-testid="unverified-reply">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="border border-carbon-gray-60 px-1.5 py-0.5 font-plex-mono text-[10px] uppercase tracking-[0.1em] text-carbon-gray-70">
          {label}
        </span>
        <span className="text-[12px] text-carbon-gray-60">
          What the sender wrote — not checked against the data.
        </span>
      </div>
      <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-[12px]">
        <dt className="text-carbon-gray-60">From</dt>
        <dd className="font-plex-mono text-carbon-ink" data-testid="reply-from">{String(d.from ?? '')}</dd>
        <dt className="text-carbon-gray-60">Subject</dt>
        <dd className="text-carbon-ink" data-testid="reply-subject">{String(d.subject ?? '')}</dd>
        <dt className="text-carbon-gray-60">Received</dt>
        <dd className="text-carbon-ink">{fmtTime(d.receivedAt)}</dd>
        <dt className="text-carbon-gray-60">Run was</dt>
        <dd className="font-plex-mono text-carbon-ink">{String(d.state ?? '—')}</dd>
      </dl>
      <pre className="mt-2 max-h-80 overflow-auto whitespace-pre-wrap break-words border border-carbon-border bg-carbon-white p-3 font-plex-mono text-[12px] text-carbon-ink" data-testid="reply-body">
        {String(d.body ?? '')}
      </pre>
      {d.bodyTruncated === true && (
        <div className="mt-1 text-[11px] text-carbon-gray-60" data-testid="reply-truncated">
          The message was longer than we store; this is the beginning of it.
        </div>
      )}
    </div>
  );
}

function SystemEvent({ event }) {
  const move = transitionOf(event);
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 px-4 py-2 text-[12px]">
      <span className="font-plex-mono text-carbon-ink">{event.type}</span>
      {move && <span className="font-plex-mono text-carbon-gray-70">{move}</span>}
      {event.data && event.data.reason && <span className="text-carbon-gray-60">{String(event.data.reason)}</span>}
      <span className="ml-auto text-carbon-gray-50">{event.actor ? `${event.actor} · ` : ''}{fmtTime(event.at)}</span>
    </div>
  );
}

export default function RunDetail({ tenantId, run, onClose }) {
  const { events, loading, error } = useRunEvents(tenantId, run && run.id);
  if (!tenantId || !run) return null;

  return (
    <section className="border border-carbon-border bg-carbon-white" data-testid="run-detail" aria-label="Run detail">
      <div className="flex items-center justify-between border-b border-carbon-border bg-carbon-gray-10 px-4 py-2">
        <span className="font-plex-mono text-[11px] uppercase tracking-[0.12em] text-carbon-gray-60">
          Run · {run.po_no || run.findingId || run.id} · {run.state}
        </span>
        {onClose && (
          <button type="button" onClick={onClose} className="font-plex-mono text-[10px] uppercase tracking-[0.08em] text-carbon-gray-70 hover:text-carbon-ink">
            Close
          </button>
        )}
      </div>
      {loading && <div className="px-4 py-3 text-sm text-carbon-gray-60" data-testid="run-detail-loading">Loading history…</div>}
      {error && <div role="alert" className="px-4 py-3 text-sm text-carbon-red-60">{error}</div>}
      {!loading && !error && events.length === 0 && (
        <div className="px-4 py-3 text-sm text-carbon-gray-60">No history on this run yet.</div>
      )}
      <ol className="divide-y divide-carbon-border" data-testid="run-events">
        {events.map((e) => (
          <li key={e.id} data-event-type={e.type}>
            {isUntrusted(e) ? <UnverifiedReply event={e} /> : <SystemEvent event={e} />}
          </li>
        ))}
      </ol>
    </section>
  );
}
